// Navbar — the app shell's top bar (banner). Single-sourced here in
// AuthedLayout; renders on every authed route and adapts to context via
// useOptionalProjectParams (the reconcile decision — domains live in this bar,
// not a separate strip; ProjectLayout never re-renders it).
//
// Zones:
//   LEFT     — <768 hamburger · cube (brand+home, md+) · PROJECT CONTEXT
//              (the always-visible operational anchor — project name + caret →
//              switch; never collapses, both layouts).
//   CENTER   — permission-gated domains (PrimaryNav), md+ only.
//   RIGHT    — search (⌘K) · notifications 🔔 · gear (the admin door, md+ — `Gear`:
//              a sysadmin's two-row menu in a project, the platform link outside)
//              · avatar. Search + notifications are reserved placeholders.
//
// On <768 the domains collapse into the hamburger drawer; project context, search,
// notifications + avatar stay in the bar (gear/switcher-list move into the
// drawer / the project dropdown). The brand wordmark lives on the pre-auth +
// project-less + about screens, not the authed shell.

import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { icons } from '../ui/icons.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useSidebarStore } from '../../stores/sidebarStore.js';
import { pageBox, pageGutter } from './pageBox.js';
import { LogoMark } from '../ui/Logo.js';
import { IconButton } from '../ui/IconButton.js';
import { PrimaryNav } from './PrimaryNav.js';
import { PlatformNav } from './PlatformNav.js';
import { SearchPlaceholder, NotificationsButton } from './ReservedTools.js';
import { ProjectSwitcher } from './ProjectSwitcher.js';
import { AccountMenu } from './AccountMenu.js';
import { Gear } from './Gear.js';
import { MobileNavDrawer } from './MobileNavDrawer.js';

export function Navbar() {
  const { t } = useTranslation('nav');
  const projectId = useOptionalProjectParams();
  const location = useLocation();
  const setMobileNavOpen = useSidebarStore((s) => s.setMobileNavOpen);

  const isPlatform = location.pathname.startsWith('/platform');

  // The band caps to the shared `pageBox` (boxed app frame, Model B): on
  // ultra-wide it stops at the measure with calm canvas wings, never a full-bleed
  // bar. Inner content carries the gutter (DESIGN-TOKENS §Content measure).
  //
  // Pinned by the FRAME, not here: `ShellTop` pins the project strips + this bar
  // as one block (the global anchor on list / admin / home, where there's no
  // surface chrome to replace it). On ENTITY pages the surface's condensed bar
  // OVERLAYS this bar on scroll (a higher z-tier, ADR-036 §9 — "one bar owns the
  // top"); the nav is never made to scroll away app-wide (that stranded
  // list/admin nav).
  return (
    <header className={`${pageBox} h-14 track-b bg-[var(--color-chrome)] text-[var(--color-text-primary)]`}>
      <div className={`flex h-full items-center gap-2 ${pageGutter}`}>
      {/* LEFT — hamburger (<768) + cube brand/home (md+) + the context control.
          The cube is the brand/home link in BOTH contexts (project → project
          home; platform → /platform/projects); the full lockup look is the cube
          plus the context control's wordmark sitting beside it. md+ only — the
          hamburger owns the left slot on phones. The context control itself (the
          project name, or the RubixEMS wordmark on platform) lives in
          ProjectSwitcher and opens the dropdown. */}
      <IconButton
        className="md:hidden"
        aria-label={t('menu.open')}
        onClick={() => setMobileNavOpen(true)}
      >
        <icons.menu aria-hidden size={22} />
      </IconButton>

      {/* LEFT ZONE — brand + context, natural width (truncating). The domains
          FLOW LEFT right after this zone: proximity reads as ownership (the
          domains belong to the context) — the enterprise-shell norm. A
          fixed-width zone (hole after short names) and centered domains
          (floating, context-less on wide screens) were both tried and
          rejected by the eye; the start position varying a little between
          portals is normal (GitHub's does too). */}
      <div className="flex items-center gap-2 min-w-0">
        <RouterLink
          to={isPlatform ? '/platform/projects' : projectId ? `/p/${projectId}/` : '/'}
          aria-label={t('homeLink')}
          className="group hidden md:flex items-center shrink-0 rounded-sm outline-none focus-ring"
        >
          {/* aria-hidden so the link's name is its purpose ('home'), not a second
              'RubixEMS' that collides with the platform switcher's wordmark. */}
          <LogoMark size={28} aria-hidden className="text-[var(--color-text-primary)]" />
        </RouterLink>

        {/* Project context — the always-visible operational anchor (left of the
            domains, both layouts); click opens the project list. Never hidden. */}
        <ProjectSwitcher projectId={projectId} />
      </div>

      {/* Domains (md+) — flow after the context; PrimaryNav scrolls within
          itself when narrow, so it never pushes the right cluster off-screen.
          Platform tabs take the same slot. */}
      {projectId && <PrimaryNav projectId={projectId} />}
      {isPlatform && <PlatformNav />}

      {/* RIGHT cluster — pinned (shrink-0), ms-auto keeps it right. Tools + admin
          + account (the switcher moved to the left). */}
      <div className="ms-auto shrink-0 flex items-center gap-1 sm:gap-2">
        {/* Search (⌘K) — leads the right zone. Reserved placeholder. */}
        <SearchPlaceholder />

        {/* Action icons clustered far-right: notifications · gear · avatar. */}
        <NotificationsButton />

        {/* Gear — desktop only (the admin door). On phones the admin sections and
            the platform door live in the hamburger drawer, so the gear would be redundant. */}
        <Gear projectId={projectId} className="hidden md:inline-flex" />

        {/* Account avatar — always visible (account actions live here at every width). */}
        <AccountMenu projectId={projectId} />
      </div>

        {/* <768 hamburger drawer — switcher + domains + account */}
        <MobileNavDrawer projectId={projectId} />
      </div>
    </header>
  );
}
